import { BaseModel, BaseModelProps, ReactRenderer } from '@mail/3promo';
import { LocalStorageStore } from '@mail/3promo/stores/localStorage';
import type { ModelCreator } from '@mail/3promo/types';
import balloonImg from 'img/family/spaceToEach/balloonImg.png';
import React from 'react';
import { IS_MOBILE_BROWSER, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { getFamilyAlbumsFeature, getFamilyFoldersFeature } from 'reactApp/modules/family/family.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { isCloudOrMailOrApiOverQuota } from 'reactApp/modules/userQuota/overquota.helpers';
import { store as reduxStore } from 'reactApp/store';
import { FamilySpaceToEachBalloonOnShow as onShow } from 'reactApp/ui/FamilySpaceToEachBalloon/FamilySpaceToEachBalloon.helpers';

export const FAMILY_SPACE_TO_EACH_BALLOON_ID = 'family-space-to-each-balloon';

class PopupModel extends BaseModel<BaseModelProps> {
    getParent(): HTMLElement | null {
        return document.documentElement;
    }
}

const LazyComponent = React.lazy(() => import('reactApp/ui/FamilySpaceToEachBalloon/FamilySpaceToEachBalloon'));

const SuspendedComponent = () => {
    return (
        <React.Suspense fallback={null}>
            <LazyComponent />
        </React.Suspense>
    );
};

export const familySpaceToEachBalloon: ModelCreator = ({ models, render, stores }) => {
    return new PopupModel(
        FAMILY_SPACE_TO_EACH_BALLOON_ID,
        models.base,
        new BaseModelProps(FAMILY_SPACE_TO_EACH_BALLOON_ID, {
            store: new LocalStorageStore(FAMILY_SPACE_TO_EACH_BALLOON_ID, stores.localStorage),
            renderer: new ReactRenderer(FAMILY_SPACE_TO_EACH_BALLOON_ID, SuspendedComponent, render.react),
            templateData: {},
            ignoreTestOnRedraw: true,
            closeLimit: 1,
            showLimit: 1,
            preloadingImages: [balloonImg],
            test: (resolve, reject) => {
                const state = reduxStore.getState();
                const storage = getCurrentStorage(state);

                if (
                    storage !== EStorageType.home &&
                    storage !== EStorageType.familyFolders &&
                    storage !== EStorageType.familyAlbums &&
                    storage !== EStorageType.gallery &&
                    storage !== EStorageType.albums &&
                    storage !== EStorageType.documents &&
                    storage !== EStorageType.feed
                ) {
                    reject();
                    return;
                }

                const isBizUser = UserSelectors.isBizUser(state);
                const isCorpUser = UserSelectors.isCorpUser(state);
                const isOverquota = isCloudOrMailOrApiOverQuota(state);
                const hasFamilyFoldersFeature = getFamilyFoldersFeature(state);
                const hasFamilyAlbumsFeature = getFamilyAlbumsFeature(state);
                const isFamilyUser = hasFamilyFoldersFeature && hasFamilyAlbumsFeature;

                const isReject = IS_WEBVIEW || IS_MOBILE_BROWSER || isBizUser || isCorpUser || isOverquota || isFamilyUser;

                if (isReject) {
                    reject();
                    return;
                }

                resolve();
            },
            onShow,
        })
    );
};
